/**
 * The parent's half of the sealed frame's handshake (gyro-core sealedFrame.ts; docs/DESIGN-html-preview-panel.md): one
 * session per frame page loaded. Pure, so it is tested without a browser; SealedFrame.tsx drives it.
 *
 * The parent acts on exactly one message from the frame, ever: the frame page's READY, from the iframe's own window, from
 * an opaque origin (the serialization "null", which only the sandbox gives), while this session is waiting for it. It then
 * posts RENDER with the page once. Everything else the frame posts (a page's own scripts posting to `top`, a second READY,
 * anything with another shape) is ignored, and nothing the frame posts is ever read for content.
 */
import { DESKTOP_SEALED_FRAME_URL, SEALED_FRAME_PATH, SEALED_FRAME_READY, SEALED_FRAME_RENDER } from '@exante/gyro-core/sealed-frame';
export class SealedFrameSession {
    html;
    frameWindow;
    phase = 'waiting';
    loads = 0;
    constructor(html, frameWindow) {
        this.html = html;
        this.frameWindow = frameWindow;
    }
    /** Whether the page has been handed to the frame page. */
    get rendered() {
        return this.phase === 'sent';
    }
    /**
     * A message on Gyro's window. Returns true only for the frame page's READY, which it answers with the page: the target is
     * '*' because a sandboxed frame's origin is opaque and matches no origin string, and the source was checked to be the
     * frame page's own window an instant before (it navigates nowhere on its own, and its page has not been shown yet).
     */
    message(event) {
        if (this.phase !== 'waiting')
            return false;
        const frame = this.frameWindow();
        if (!frame || event.source !== frame || event.origin !== 'null')
            return false;
        const data = event.data;
        if (!data || typeof data !== 'object' || data.type !== SEALED_FRAME_READY)
            return false;
        this.phase = 'sent';
        frame.postMessage({ type: SEALED_FRAME_RENDER, html: this.html }, '*');
        return true;
    }
    /**
     * The iframe's load event. The first is the frame page; any later one means the frame itself navigated, which only its
     * page could have done: the caller starts it again (and says so).
     */
    load() {
        this.loads += 1;
        return this.loads === 1 ? 'first' : 'navigated';
    }
}
/** The frame page's address: the desktop app's own host, else gyro-svc's route under the widget's base URL. */
export function sealedFrameUrl(options) {
    return options.desktopShell ? DESKTOP_SEALED_FRAME_URL : `${options.baseUrl.replace(/\/+$/, '')}${SEALED_FRAME_PATH}`;
}
/** The origin a frame address loads from, against the page's own address (a relative base URL is the page's origin). */
export function frameOrigin(frameUrl, pageHref) {
    try {
        return new URL(frameUrl, pageHref).origin;
    }
    catch {
        return null;
    }
}
/**
 * Whether a CSP violation on Gyro's page refused the frame itself: a frame-src (or child-src) violation whose blocked
 * address is on the frame's origin. A host page whose own policy does not allow gyro-svc's origin in frame-src says so at
 * once, and the panel shows "Preview isn't available in this app" without waiting for the frame page.
 */
export function refusedFrame(violation, origin) {
    const directive = violation.effectiveDirective || violation.violatedDirective || '';
    if (!/^(?:frame-src|child-src)\b/.test(directive) || !origin)
        return false;
    try {
        return new URL(violation.blockedURI ?? '').origin === origin;
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=sealedFrameSession.js.map